<script setup>
import { computed, ref } from 'vue';

import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  hint: {
    type: String,
    default: '',
  },
  summary: {
    type: String,
    required: true,
  },
  selectAllLabel: {
    type: String,
    required: true,
  },
  options: {
    type: Array,
    default: () => [],
  },
});

const selected = defineModel({
  type: Array,
  default: () => [],
});

const open = ref(false);

const allSelected = computed(
  () =>
    props.options.length > 0 &&
    props.options.every(option => selected.value.includes(option.value))
);

const someSelected = computed(
  () => selected.value.length > 0 && !allSelected.value
);

const toggle = value => {
  if (selected.value.includes(value)) {
    selected.value = selected.value.filter(item => item !== value);
    return;
  }
  selected.value = [...selected.value, value];
};

const toggleAll = () => {
  selected.value = allSelected.value
    ? []
    : props.options.map(option => option.value);
};
</script>

<template>
  <div v-on-clickaway="() => (open = false)" class="relative min-w-0">
    <p class="mb-0 text-sm font-medium text-n-slate-12">
      {{ label }}
    </p>
    <div class="relative">
      <button
        type="button"
        class="mt-1.5 flex h-10 w-full items-center justify-between gap-2 rounded-lg bg-n-alpha-black2 px-3 text-left text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak transition-all duration-200 hover:outline-n-slate-6 focus:outline-n-brand"
        :aria-expanded="open"
        @click="open = !open"
      >
        <span class="truncate">{{ summary }}</span>
        <Icon
          icon="i-lucide-chevron-down"
          class="size-4 shrink-0 text-n-slate-11"
        />
      </button>
      <div
        v-if="open"
        class="absolute left-0 right-0 top-full z-30 mt-1 max-h-64 overflow-y-auto rounded-lg bg-n-solid-2 p-2 shadow-lg outline outline-1 outline-n-container"
      >
        <label
          v-if="options.length"
          class="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-2"
        >
          <Checkbox
            :model-value="allSelected"
            :indeterminate="someSelected"
            @change="toggleAll"
          />
          {{ selectAllLabel }}
        </label>
        <label
          v-for="option in options"
          :key="option.value"
          class="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-n-slate-11 hover:bg-n-alpha-2"
        >
          <Checkbox
            :model-value="selected.includes(option.value)"
            @change="toggle(option.value)"
          />
          {{ option.label }}
        </label>
      </div>
    </div>
    <p v-if="hint" class="mb-0 mt-2 text-xs text-n-slate-10">
      {{ hint }}
    </p>
  </div>
</template>
